import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Dropdown, Form} from 'react-bootstrap'
import Select from 'react-select'
import Tooltip from 'react-bootstrap/Tooltip'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import {Formik} from 'formik'
import {KTSVG} from '../../../_metronic/helpers'
import LazySelect from '../../components/LazySelect'
import {useCustomers} from '../../hooks/useGlobalData'
import {
  batchesForCustomers,
  batchesForFinanceTab,
  BulkVosFinanceBatchOption,
  BulkVosFinanceFilterData,
  buildDefaultFinanceFilter,
  formatFinanceBatchLabel,
  isBulkVosFinanceFilterActive,
} from './bulkVosFinanceFilter'

type Props = {
  filterData: BulkVosFinanceFilterData
  availableBatches: BulkVosFinanceBatchOption[]
  onApply: (next: BulkVosFinanceFilterData) => void
}

const batchTabOptions = [
  {value: 'pending', label: 'Pending'},
  {value: 'approved', label: 'Approved'},
]

type SelectOption = {value: string; label: string}

function buildBatchOptions(
  availableBatches: BulkVosFinanceBatchOption[],
  batchTab: BulkVosFinanceFilterData['batchTab'],
  customerIds: SelectOption[],
): SelectOption[] {
  const ids = customerIds.map((c) => String(c.value).trim()).filter(Boolean)
  const forTab = batchesForFinanceTab(availableBatches, batchTab)
  return batchesForCustomers(forTab, ids).map((b) => ({
    value: String(b.batchId),
    label: formatFinanceBatchLabel(b),
  }))
}

export function BulkVosFinanceFilterDropdown({
  filterData,
  availableBatches,
  onApply,
}: Props) {
  const [showDropdownMenu, setShowDropdownMenu] = useState(false)
  const [formSessionKey, setFormSessionKey] = useState(0)
  const [isFilterApplied, setIsFilterApplied] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const {data: customersData, isLoading: isFetchingCustomers} = useCustomers()

  const handleClickOutside = useCallback((event: MouseEvent) => {
    const target = event.target as Node
    const isInsideDropdown = dropdownRef.current?.contains(target)
    const el = target as Element
    const isInsideSelectMenu =
      el?.closest?.('.react-select__menu') != null ||
      el?.closest?.('.react-select__menu-portal') != null
    if (!isInsideDropdown && !isInsideSelectMenu) {
      setShowDropdownMenu(false)
    }
  }, [])

  useEffect(() => {
    if (!showDropdownMenu) return
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showDropdownMenu, handleClickOutside])

  useEffect(() => {
    setIsFilterApplied(isBulkVosFinanceFilterActive(filterData, availableBatches))
  }, [filterData, availableBatches])

  const customerOptions = useMemo(
    () =>
      customersData?.map((d) => {
        const raw = d as {key?: string; Key?: string; value?: string; Value?: string}
        return {
          value: String(raw.key ?? raw.Key ?? '').trim(),
          label: String(raw.value ?? raw.Value ?? '').trim(),
        }
      }).filter((o) => o.value) ?? [],
    [customersData],
  )

  const appliedBatchOption = useMemo(() => {
    if (filterData.batchId == null) return null
    const row = availableBatches.find((b) => b.batchId === filterData.batchId)
    if (!row) return null
    return {value: String(row.batchId), label: formatFinanceBatchLabel(row)}
  }, [availableBatches, filterData.batchId])

  const handleReset = () => {
    const cleared = buildDefaultFinanceFilter(availableBatches)
    onApply(cleared)
    setIsFilterApplied(false)
    setShowDropdownMenu(false)
  }

  const buildFilterFromValues = (values: {
    customerIds?: SelectOption[]
    batchTab?: SelectOption
    batch?: SelectOption | null
  }): BulkVosFinanceFilterData => ({
    customerIds: values.customerIds?.map((o) => o.value) ?? [],
    batchTab: (values.batchTab?.value === 'approved' ? 'approved' : 'pending') as BulkVosFinanceFilterData['batchTab'],
    batchId: values.batch?.value ? Number(values.batch.value) : null,
  })

  return (
    <Dropdown
      ref={dropdownRef}
      autoClose={false}
      show={showDropdownMenu}
      onToggle={(nextShow) => {
        setShowDropdownMenu(nextShow)
        if (nextShow) setFormSessionKey((k) => k + 1)
      }}
    >
      <OverlayTrigger placement='top' overlay={<Tooltip id='bulk-vos-filter'>Filter</Tooltip>}>
        <Dropdown.Toggle
          variant='primary'
          id='bulk-vos-finance-filter'
          className={`btn btn-icon btn-sm no-caret ${isFilterApplied ? 'btn-light-danger' : ''}`}
        >
          <KTSVG path='/media/icons/duotune/general/gen031.svg' className='svg-icon-primary' />
        </Dropdown.Toggle>
      </OverlayTrigger>
      {showDropdownMenu && (
        <Formik
          key={formSessionKey}
          initialValues={{
            customerIds:
              customerOptions.filter((d) => filterData.customerIds.includes(d.value)) ?? [],
            batchTab:
              batchTabOptions.find((o) => o.value === filterData.batchTab) ?? batchTabOptions[0],
            batch: appliedBatchOption,
          }}
          onSubmit={(values, {setSubmitting}) => {
            setSubmitting(true)
            try {
              const next = buildFilterFromValues(values)
              onApply(next)
              setIsFilterApplied(isBulkVosFinanceFilterActive(next, availableBatches))
            } finally {
              setShowDropdownMenu(false)
              setSubmitting(false)
            }
          }}
        >
          {({setFieldValue, values, isSubmitting, handleSubmit, resetForm}) => {
            const batchTab =
              values.batchTab?.value === 'approved' ? 'approved' : ('pending' as const)
            const selectedCustomerIds = (values.customerIds ?? [])
              .map((c) => String(c?.value ?? '').trim())
              .filter(Boolean)
            const batchOptions = buildBatchOptions(
              availableBatches,
              batchTab,
              selectedCustomerIds.map((id) => ({value: id, label: id})),
            )
            const customersSelected = selectedCustomerIds.length > 0

            return (
              <Form>
                <Dropdown.Menu
                  className='p-4 w-350px'
                  style={{minHeight: '100%', overflow: 'visible'}}
                >
                  <div className='px-4 pb-4'>
                    <div className='fs-4 text-dark fw-semibolder'>Filter</div>
                  </div>
                  <div className='separator border-gray-200' />
                  <div className='card px-4 mt-4'>
                    <div className='row flex-column'>
                      <div className='col-sm-12 col-md-12 mb-3'>
                        <label className='form-label fs-7 mb-1'>Dispute status</label>
                        <Select
                          className='react-select-styled react-select-solid react-select-sm'
                          classNamePrefix='react-select'
                          options={batchTabOptions}
                          value={values.batchTab}
                          menuPortalTarget={document.body}
                          menuPosition='fixed'
                          onChange={(e: unknown) => {
                            setFieldValue('batchTab', e)
                            setFieldValue('batch', null)
                          }}
                          styles={{
                            control: (provided: any) => ({
                              ...provided,
                              width: '100%',
                              border: '1px solid #e4e6ef',
                            }),
                            menuPortal: (provided: any) => ({...provided, zIndex: 10050}),
                          }}
                        />
                      </div>

                      <div className='col-sm-12 col-md-12'>
                        <div className='mb-3'>
                          <label className='form-label fs-7 mb-1'>Customer(s)</label>
                          <LazySelect
                            name='customerIds'
                            options={!isFetchingCustomers ? customerOptions : []}
                            placeholder='Select Customer(s)'
                            isMulti
                            isLoading={isFetchingCustomers}
                            value={values.customerIds}
                            menuPortalTarget={document.body}
                            onChange={(e: unknown) => {
                              const next = Array.isArray(e) ? e : e ? [e] : []
                              setFieldValue('customerIds', next)
                              setFieldValue('batch', null)
                            }}
                          />
                        </div>
                      </div>

                      <div className='col-sm-12 col-md-12 mb-3'>
                        <label className='form-label fs-7 mb-1'>Batch</label>
                        <Select
                          className='react-select-styled react-select-solid react-select-sm'
                          classNamePrefix='react-select'
                          options={batchOptions}
                          value={values.batch}
                          menuPortalTarget={document.body}
                          menuPosition='fixed'
                          placeholder={
                            batchOptions.length
                              ? customersSelected
                                ? 'Select batch'
                                : 'Select batch (all customers)'
                              : customersSelected
                                ? 'No batches for selected customer(s)'
                                : 'No batches for this status'
                          }
                          onChange={(e: unknown) => {
                            setFieldValue('batch', e)
                            const opt = e as SelectOption | null
                            if (!opt?.value) return
                            const row = availableBatches.find(
                              (b) => String(b.batchId) === String(opt.value),
                            )
                            if (!row) return
                            const customerOpt = customerOptions.find(
                              (c) => c.value === String(row.customerId),
                            )
                            if (customerOpt) {
                              setFieldValue('customerIds', [customerOpt])
                            }
                          }}
                          isClearable
                          styles={{
                            control: (provided: any) => ({
                              ...provided,
                              width: '100%',
                              border: '1px solid #e4e6ef',
                            }),
                            menuPortal: (provided: any) => ({...provided, zIndex: 10050}),
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className='text-end pt-5 px-4'>
                    <button
                      type='reset'
                      className='btn btn-sm btn-light me-2'
                      onClick={() => {
                        handleReset()
                        resetForm()
                      }}
                    >
                      Reset
                    </button>
                    <button
                      type='button'
                      className='btn btn-sm btn-primary'
                      onClick={() => handleSubmit()}
                      disabled={isSubmitting}
                    >
                      Apply
                    </button>
                  </div>
                </Dropdown.Menu>
              </Form>
            )
          }}
        </Formik>
      )}
    </Dropdown>
  )
}
